(() => {
  const selector = '.nc-app-carousel, .nc-product-carousel';

  if (window.NutricellsAutoCarousel) {
    window.NutricellsAutoCarousel.init(document);
    return;
  }

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  class AutoCarousel {
    constructor(element) {
      this.element = element;
      this.items = Array.from(element.children).filter((item) => !item.hasAttribute('data-nc-carousel-clone'));
      if (this.items.length < 2) return;

      this.speed = Number(element.dataset.ncCarouselSpeed) || 30;
      this.resumeTimer = null;

      element.dataset.ncCarouselReady = 'true';
      element.setAttribute('aria-live', 'off');
      if (!element.hasAttribute('tabindex')) element.tabIndex = 0;
      element.scrollLeft = 0;

      this.appendClones();
      this.bindEvents();
      this.recalculate();
      this.updateMotionPreference();
    }

    appendClones() {
      this.clones = this.items.map((item) => {
        const clone = item.cloneNode(true);
        clone.setAttribute('aria-hidden', 'true');
        clone.setAttribute('data-nc-carousel-clone', 'true');
        clone.removeAttribute('id');
        Array.from(clone.attributes).forEach((attribute) => {
          if (attribute.name.startsWith('data-shopify')) clone.removeAttribute(attribute.name);
        });
        clone.querySelectorAll('[id]').forEach((node) => node.removeAttribute('id'));
        clone
          .querySelectorAll('a, button, input, select, textarea, [tabindex]')
          .forEach((node) => node.setAttribute('tabindex', '-1'));
        this.element.appendChild(clone);
        return clone;
      });
      this.firstClone = this.clones[0];
    }

    bindEvents() {
      this.element.addEventListener('touchstart', () => this.setInteracting(true), { passive: true });
      this.element.addEventListener('touchend', () => this.pauseTemporarily(1200), { passive: true });
      this.element.addEventListener('touchcancel', () => this.pauseTemporarily(1200), { passive: true });
      this.element.addEventListener('wheel', () => this.pauseTemporarily(900), { passive: true });

      if (typeof reducedMotion.addEventListener === 'function') {
        reducedMotion.addEventListener('change', () => this.updateMotionPreference());
      } else {
        reducedMotion.addListener(() => this.updateMotionPreference());
      }

      if ('IntersectionObserver' in window) {
        this.intersectionObserver = new IntersectionObserver(
          (entries) => {
            this.element.classList.toggle('nc-carousel--offscreen', !(entries[0]?.isIntersecting ?? true));
          },
          { rootMargin: '120px 0px' }
        );
        this.intersectionObserver.observe(this.element);
      }

      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => this.recalculate());
        this.resizeObserver.observe(this.element);
        this.resizeObserver.observe(this.items[0]);
        this.resizeObserver.observe(this.firstClone);
      } else {
        window.addEventListener('resize', () => this.recalculate(), { passive: true });
      }
    }

    recalculate() {
      if (!this.firstClone || !this.items[0]) return;
      const loopWidth = this.firstClone.offsetLeft - this.items[0].offsetLeft;
      if (loopWidth <= 0) return;
      this.element.style.setProperty('--nc-carousel-translate', `-${loopWidth}px`);
      this.element.style.setProperty('--nc-carousel-duration', `${loopWidth / this.speed}s`);
    }

    setInteracting(active) {
      this.element.classList.toggle('nc-carousel--interacting', active);
    }

    pauseTemporarily(delay) {
      this.setInteracting(true);
      window.clearTimeout(this.resumeTimer);
      this.resumeTimer = window.setTimeout(() => this.setInteracting(false), delay);
    }

    updateMotionPreference() {
      this.element.classList.toggle('nc-carousel--autoplay', !reducedMotion.matches);
    }
  }

  const init = (root = document) => {
    const carousels = [];
    if (root.matches?.(selector)) carousels.push(root);
    root.querySelectorAll?.(selector).forEach((element) => carousels.push(element));

    carousels.forEach((element) => {
      if (!element.hasAttribute('data-nc-carousel-ready')) new AutoCarousel(element);
    });
  };

  window.NutricellsAutoCarousel = { init };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(document), { once: true });
  } else {
    init(document);
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
